/**
 * PortalApp - Complete portal application extending WebApp
 * Provides built-in navigation, sidebar, and content management
 * Clean, simple implementation that reuses WebApp and View logic
 */

import WebApp from '@core/WebApp.js';
import TopNav from '@core/views/navigation/TopNav.js';
import Sidebar from '@core/views/navigation/Sidebar.js';
import PageHeader from '@core/views/navigation/PageHeader.js';
import DeniedPage from '@core/pages/DeniedPage.js';
import TokenManager from '@core/services/TokenManager.js';
import {User} from '@core/models/User.js';
import {Group } from '@core/models/Group.js';
import {Member} from '@core/models/Member.js';
import NotFoundPage from '@core/pages/NotFoundPage.js';
import ToastService from '@core/services/ToastService.js';
import Modal from '@core/views/feedback/Modal.js';

export default class PortalApp extends WebApp {
    constructor(config = {}) {
        // Pass core WebApp config through
        super(config);

        this.sidebarConfig = config.sidebar;
        // Portal-specific configuration (clean flat structure)
        // if (config.sidebar && config.sidebar.menus) {
        //     this.sidebarConfig.menus = config.sidebar.menus;
        //     this.sidebarConfig.groupSelectorMode = config.sidebar.groupSelectorMode || "inline";
        //     if (config.sidebar.groupHeader) {
        //         this.sidebarConfig.groupHeader = config.sidebar.groupHeader;
        //     }
        // } else if (config.sidebar.menu) {
        //     this.sidebarConfig.menu = config.sidebar.menu;
        // } else if (config.sidebar.items) {
        //     this.sidebarConfig.menu = config.sidebar;
        // }

        this.topbarConfig = config.topbar || {};

        // Legacy support - topnav -> topbar
        if (config.topnav && !config.topbar) {
            this.topbarConfig = config.topnav;
        }

        // Page header configuration
        this.showPageHeader = config.showPageHeader || false;
        this.pageHeaderConfig = config.pageHeader || {};

        // Portal components
        this.sidebar = null;
        this.topbar = null;
        this.topnav = null; // Legacy reference
        this.pageHeader = null;
        this.tokenManager = new TokenManager();
        this._installAuthGate();

        // Active group management
        this.activeGroup = null;
        // Portal state - Load from localStorage first, then fallback to config
        if (!this.isMobile()) {
            this.sidebarCollapsed = this.loadSidebarState() ??
                (this.sidebarConfig.defaultCollapsed || false);
        } else {
            this.sidebarCollapsed = this.sidebarConfig.defaultCollapsed || false;
        }
        this.setupPageContainer();

        this.toast = new ToastService();
        this.Dialog = Modal;  // Backwards compat — prefer app.modal instead

        this.registerPage("denied", DeniedPage);
        this.registerPage("404", NotFoundPage);
    }

    /**
     * Override WebApp start to setup portal layout
     */
    async start() {
        // Call parent start (handles router, error handling, etc.)
        // Setup router
        await this.checkAuthStatus();

        this.events.on('auth:unauthorized', () => {
            this.tokenManager.clearTokens();
            this.rest.clearAuth();
            this.setActiveUser(null);
            return;
        });

        this.events.on('auth:logout', () => {
            this.tokenManager.clearTokens();
            this.rest.clearAuth();
            this.setActiveUser(null);
            return;
        });

        this.events.on("browser:focus", () => {
            if (!this.activeUser) return;
            this.tokenManager.checkAndRefreshTokens(this);
        });

        this.events.on('portal:action', this.onPortalAction.bind(this));

        if (this.activeUser) {
            // Check and load active group after auth
            await this.checkActiveGroup();
        }

        await this.setupRouter();

        // Mark as started
        this.isStarted = true;

        // Emit app ready event
        this.events.emit('app:ready', { app: this });

        // Prompt passkey setup after login if not dismissed
        if (this.activeUser && !this.activeUser.get("has_passkey")) {
            this.showPasskeySetup();
        }


    }

    async checkAuthStatus() {
        // Redeem an inbound cross-origin handoff code (?auth_code=…) before
        // deciding the user is unauthenticated. Single-flight guard inside
        // handleAuthCodeFromURL makes this safe to call from multiple boot
        // paths. No-op when the param is absent.
        await this.tokenManager.handleAuthCodeFromURL(this);

        const tokenStatus = this.tokenManager.checkTokenStatus();

        // Handle logout scenarios
        if (tokenStatus.action === 'logout') {
            this.events.emit('auth:unauthorized', { app: this });
            return false;
        }

        // Handle refresh scenarios - attempt refresh if needed
        if (tokenStatus.action === 'refresh') {
            const refreshed = await this.tokenManager.checkAndRefreshTokens(this);
            if (!refreshed) {
                // If refresh failed, checkAndRefreshTokens already handled logout
                return false;
            }
        }

        // At this point we have a valid token
        const token = this.tokenManager.getTokenInstance();

        // If user already loaded, just start auto-refresh and return
        if (this.activeUser) {
            this.tokenManager.startAutoRefresh(this);
            return true;
        }

        // Load user data
        this.rest.setAuthToken(token.token);
        const user = new User({ id: token.getUserId() });
        const resp = await user.fetch();
        if (!resp.success) {
            this.tokenManager.clearTokens();
            this.events.emit('auth:unauthorized', { app: this, error: resp.error });
            return false;
        }

        this.setActiveUser(user);
        this.tokenManager.startAutoRefresh(this);
        return true;
    }

    /**
     * Install a pre-request interceptor that blocks outgoing REST calls on
     * access-token validity. Expired access tokens trigger a (single-flight)
     * refresh before the request goes out. When both tokens are invalid the
     * request is rejected with a 401 response without hitting the network
     * and `auth:unauthorized` is emitted.
     *
     * Bypassed for:
     *   - `/api/token/refresh` — required to avoid recursion when the gate
     *     itself triggers a refresh.
     *   - Requests made while no access token is stored (pre-login / public).
     * @private
     */
    _installAuthGate() {
        this.rest.addInterceptor('request', async (request) => {
            // Bypass only the refresh endpoint itself (prevents recursion).
            // Login / other auth endpoints fall through the no-token bypass
            // below when used during pre-login flows.
            let pathname = '';
            try {
                pathname = new URL(request.url, window.location.origin).pathname;
            } catch (_e) {
                pathname = request.url || '';
            }
            if (pathname === '/api/token/refresh') {
                return request;
            }

            // No stored token means we're in a pre-login / public flow.
            if (!this.tokenManager.getToken()) {
                return request;
            }

            // Throws AuthRequiredError if the token cannot be refreshed.
            // Rest.request() recognizes the error and short-circuits to a
            // 401 response without calling fetch.
            await this.tokenManager.ensureValidToken(this);

            // request.headers was snapshotted from config.headers before this
            // interceptor ran, so after a successful refresh we need to
            // overwrite the Authorization header with the new token.
            const authHeader = this.tokenManager.getAuthHeader();
            if (authHeader) {
                request.headers['Authorization'] = authHeader;
            }

            return request;
        });
    }

    /**
     * Check and load active group from storage
     */
    async checkActiveGroup() {
        // First check URL search params for group parameter
        const urlParams = new URLSearchParams(window.location.search);
        const urlGroupId = urlParams.get('group');

        // Determine which group ID to use: URL param takes priority
        const groupId = urlGroupId || this.loadActiveGroupId();

        if (groupId) {
            try {
                const group = new Group({ id: groupId });
                const resp = await group.fetch();
                if (!resp.success || !resp.data.status) {
                    this.clearActiveGroup();
                    console.warn('Failed to load active group:', resp.statusText);
                    return;
                }

                this.activeGroup = group;
                // If we got the group from URL, save it as the new active group
                if (urlGroupId) {
                    this.saveActiveGroupId(groupId);
                }

                if (this.activeUser) {
                    this.activeUser.member = new Member();
                    await this.activeUser.member.fetchForGroup(group.id);
                }

                // Emit event that group was loaded
                this.events.emit('group:loaded', { group: this.activeGroup });


            } catch (error) {
                console.warn('Failed to load active group:', error);
                // If URL group failed, try to clear it and fall back to stored group
                if (urlGroupId && !this.loadActiveGroupId()) {
                    // URL group failed and no stored group, clear everything
                    this.clearActiveGroupId();
                } else if (urlGroupId) {
                    // URL group failed but we have a stored group, try that instead
                    const storedGroupId = this.loadActiveGroupId();
                    if (storedGroupId && storedGroupId !== urlGroupId) {
                        try {
                            const fallbackGroup = new Group({ id: storedGroupId });
                            await fallbackGroup.fetch();
                            this.activeGroup = fallbackGroup;
                            this.events.emit('group:loaded', { group: this.activeGroup });

                        } catch (fallbackError) {
                            console.warn('Fallback to stored group also failed:', fallbackError);
                            this.clearActiveGroupId();
                        }
                    }
                }
            }
        }
    }


    /**
     * Set the active group
     */
    async setActiveGroup(group) {
        const previousGroup = this.activeGroup;
        this.activeGroup = group;

        // Save to storage
        if (group && group.get('id')) {
            this.saveActiveGroupId(group.get('id'));
        } else {
            this.clearActiveGroupId();
        }

        if (this.activeUser) {
            this.activeUser.member = new Member();
            await this.activeUser.member.fetchForGroup(group.id);
        }

        // Emit event
        this.events.emit('group:changed', {
            group,
            previousGroup,
            app: this
        });

        const page = this.getCurrentPage();
        if (page) {
            page.onGroupChange(group);
        }

        this.router.updateUrl({group:group.id}, { replace: true });

        return this;
    }

    /**
     * Get the active group
     */
    getActiveGroup() {
        return this.activeGroup;
    }

    /**
     * Clear the active group
     */
    async clearActiveGroup() {
        const previousGroup = this.activeGroup;
        this.activeGroup = null;
        this.clearActiveGroupId();
        // Emit event
        this.events.emit('group:cleared', {
            previousGroup,
            app: this
        });
        return this;
    }

    /**
     * Save active group ID to localStorage
     */
    saveActiveGroupId(groupId) {
        try {
            const key = this.getActiveGroupStorageKey();
            localStorage.setItem(key, groupId.toString());
        } catch (error) {
            console.warn('Failed to save active group ID:', error);
        }
    }

    /**
     * Load active group ID from localStorage
     */
    loadActiveGroupId() {
        try {
            const key = this.getActiveGroupStorageKey();
            return localStorage.getItem(key);
        } catch (error) {
            console.warn('Failed to load active group ID:', error);
            return null;
        }
    }

    /**
     * Clear active group ID from localStorage
     */
    clearActiveGroupId() {
        try {
            const key = this.getActiveGroupStorageKey();
            localStorage.removeItem(key);
        } catch (error) {
            console.warn('Failed to clear active group ID:', error);
        }
    }

    /**
     * Get storage key for active group ID
     */
    getActiveGroupStorageKey() {
        return `active_group_id`;
    }

    /**
     * Set portal profile to localStorage
     */
    setPortalProfile(profile) {
        try {
            localStorage.setItem('portal_profile', profile);
        } catch (error) {
            console.warn('Failed to save portal profile:', error);
        }
    }

    /**
     * Check if user needs to select a group
     */
    needsGroupSelection() {
        return !this.activeGroup;
    }

    /**
     * Setup layout based on configuration
     */
    setupPageContainer() {
        const container = typeof this.container === 'string'
            ? document.querySelector(this.container)
            : this.container;

        if (!container) {
            throw new Error(`Portal container not found: ${this.container}`);
        }

        // Create clean portal layout
        const showSidebar = this.sidebarConfig && Object.keys(this.sidebarConfig).length > 0;
        const showTopbar = this.topbarConfig && Object.keys(this.topbarConfig).length > 0;

        // If page header is enabled, wrap content in two containers
        const contentMarkup = this.showPageHeader ? `
            <div class="portal-content" id="portal-content">
                <div id="page-header"></div>
                <div id="page-container">
                    <!-- Pages render here -->
                </div>
            </div>
        ` : `
            <div class="portal-content" id="page-container">
                <!-- Pages render here -->
            </div>
        `;

        container.innerHTML = `
            <div class="portal-layout hide-sidebar">
                ${showSidebar ? '<div id="portal-sidebar"></div>' : ''}
                <div class="portal-body">
                    ${showTopbar ? '<div id="portal-topnav"></div>' : ''}
                    ${contentMarkup}
                </div>
            </div>
        `;

        // Set page container for WebApp
        this.pageContainer = '#page-container';

        // Add portal CSS classes and apply saved state
        container.classList.add('portal-container');

        // Setup page container
        this.setupPortalComponents();

        // Apply the saved sidebar state
        this.applySidebarState(container);
    }

    /**
     * Setup portal components
     */
    async setupPortalComponents() {
        await this.setupSidebar();
        await this.setupTopbar();
        await this.setupPageHeader();
        this.setupPortalEvents();
    }

    /**
     * Setup sidebar component
     */
    async setupSidebar() {
        if (!this.sidebarConfig || Object.keys(this.sidebarConfig).length === 0) return;

        this.sidebar = new Sidebar({
            containerId: 'portal-sidebar',
            ...this.sidebarConfig
        });

        await this.sidebar.render();
    }

    /**
     * Setup topbar component
     */
    async setupTopbar() {
        if (!this.topbarConfig || Object.keys(this.topbarConfig).length === 0) return;

        // Auto-inject Light / Dark / System items into the user menu unless
        // the consuming app explicitly opts out with `topbar.themeToggle: false`.
        this._injectThemeToggleItems();

        // Map config to TopNav format
        this.topbar = new TopNav({
            containerId: "portal-topnav",
            brandText: this.topbarConfig.brand || this.brand || this.title,
            brandRoute: this.topbarConfig.brandRoute || '/',
            brandIcon: this.topbarConfig.brandIcon || this.brandIcon,
            navItems: this.topbarConfig.leftItems || [],
            rightItems: this.topbarConfig.rightItems || [],
            displayMode: this.topbarConfig.displayMode || 'both',
            showSidebarToggle: this.topbarConfig.showSidebarToggle || false,
            ...this.topbarConfig
        });

        await this.topbar.render();

        // Legacy support
        this.topnav = this.topbar;
    }

    /**
     * Inject a single `Theme settings` entry into the topbar usermenu.
     * Clicking it opens `app.showThemeSettings()` — a small dialog with
     * Light / Dark / System radios. Idempotent — safe to call multiple
     * times. No-op if `topbar.themeToggle === false` or no usermenu is
     * configured.
     * @private
     */
    _injectThemeToggleItems() {
        if (this.topbarConfig.themeToggle === false) return;

        const userMenu = this._findUserMenuConfig();
        if (!userMenu || !Array.isArray(userMenu.items)) return;
        if (userMenu.items.some(item => item && item._themeToggle)) return;

        const themeItem = {
            _themeToggle: true,
            label: 'Theme settings',
            icon: 'bi-palette',
            action: 'theme-settings'
        };

        // Insert before the trailing logout (and any divider that precedes it),
        // so the existing visual grouping is preserved.
        const items = userMenu.items;
        const logoutIdx = items.findIndex(item => item && item.action === 'logout');
        const consumerDividerBeforeLogout =
            logoutIdx > 0 && items[logoutIdx - 1] && items[logoutIdx - 1].divider;
        const insertIdx = (logoutIdx === -1)
            ? items.length
            : (consumerDividerBeforeLogout ? logoutIdx - 1 : logoutIdx);

        // Only add the trailing framework divider when one isn't already
        // sitting between us and the logout — otherwise we'd render two
        // dividers in a row.
        const block = [{ _themeToggle: true, divider: true }, themeItem];
        if (logoutIdx !== -1 && !consumerDividerBeforeLogout) {
            block.push({ _themeToggle: true, divider: true });
        }
        items.splice(insertIdx, 0, ...block);
    }

    /**
     * Locate the user menu inside the topbar config (handles both the
     * `userMenu` shorthand and a `rightItems` entry tagged `id: 'user'`).
     * @private
     */
    _findUserMenuConfig() {
        if (this.topbarConfig.userMenu) return this.topbarConfig.userMenu;
        if (Array.isArray(this.topbarConfig.rightItems)) {
            return this.topbarConfig.rightItems.find(item => item && item.id === 'user') || null;
        }
        return null;
    }

    /**
     * Setup page header component
     */
    async setupPageHeader() {
        if (!this.showPageHeader) return;

        this.pageHeader = new PageHeader({
            containerId: 'page-header',
            style: this.pageHeaderConfig.style || 'default',
            showIcon: this.pageHeaderConfig.showIcon !== false,
            showDescription: this.pageHeaderConfig.showDescription !== false,
            showBreadcrumbs: this.pageHeaderConfig.showBreadcrumbs || false,
            ...this.pageHeaderConfig
        });

        // Render into the portal-content container
        const headerContainer = document.getElementById('page-header');
        if (headerContainer) {
            await this.pageHeader.render(true, headerContainer);
        }
    }

    /**
     * Setup portal event handling
     */
    setupPortalEvents() {
        // Handle sidebar toggle via event delegation
        document.addEventListener('click', (event) => {
            if (event.target.closest('[data-action="toggle-sidebar"]')) {
                event.preventDefault();
                this.toggleSidebar();
            }
        });

        // Handle responsive changes
        if (window.ResizeObserver) {
            const resizeObserver = new ResizeObserver(() => {
                this.handleResponsive();
            });
            resizeObserver.observe(document.body);
            this._resizeObserver = resizeObserver;
        } else {
            // Fallback for older browsers
            this._resizeHandler = () => this.handleResponsive();
            window.addEventListener('resize', this._resizeHandler);
        }

        // Initial responsive setup
        this.handleResponsive();
    }

    /**
     * Toggle sidebar state
     */
    toggleSidebar() {
        if (!this.sidebar) return;

        const container = document.querySelector('.portal-container');
        const isMobile = this.isMobile();

        if (isMobile) {
            container.classList.toggle('hide-sidebar');
        } else {
            container.classList.toggle('collapse-sidebar');
            this.sidebarCollapsed = !this.sidebarCollapsed;

            // Save the new state
            this.saveSidebarState(this.sidebarCollapsed);
        }

        this.events.emit('sidebar:toggled', {
            collapsed: this.sidebarCollapsed,
            mobile: isMobile
        });
    }

    /**
     * Handle responsive layout
     */
    handleResponsive() {
        const container = document.querySelector('.portal-container');
        if (!container) return;
        const isMobile = this.isMobile();

        if (isMobile) {
            container.classList.add('mobile-layout');
            if (!container.classList.contains('hide-sidebar')) {
                container.classList.add('hide-sidebar');
            }
        } else {
            container.classList.remove('mobile-layout', 'hide-sidebar');
        }

        this.events.emit('responsive:changed', { mobile: isMobile });
    }

    getPortalContainer() {
        return document.querySelector('.portal-container');
    }

    isMobile() {
        return window.innerWidth < 768;
    }

    hasMobileLayout() {
        return this.getPortalContainer().classList.contains('mobile-layout');
    }

    /**
     * Override showPage to update navigation
     */
    async showPage(page, query = {}, params = {}, options = {}) {
        const result = await super.showPage(page, query, params, options);

        if (this.hasMobileLayout()) {
            this.getPortalContainer().classList.add('hide-sidebar');
        }

        if (this.currentPage) {
            this.updateNavigation(this.currentPage);
        }

        return result;
    }

    /**
     * Update navigation active states
     */
    updateNavigation(page) {
        // Update sidebar active state
        if (this.sidebar && this.sidebar.setActivePage) {
            this.sidebar.setActivePage(page.route);
        }

        // Update topbar active state
        if (this.topbar && this.topbar.setActivePage) {
            this.topbar.setActivePage(page.route);
        }

        // Update page header
        if (this.pageHeader) {
            this.pageHeader.setPage(page);
        }

        this.events.emit('portal:page-changed', { page });
    }

    /**
     * Set active user
     */
    setActiveUser(user) {
        this.activeUser = user;

        if (this.topbar) {
            this.topbar.setUser(user);
        }


        this.events.emit('portal:user-changed', { user });
    }

    /**
     * Get the active user (for backward compatibility)
     */
    getActiveUser() {
        return this.activeUser;
    }

    /**
     * Save sidebar state to localStorage
     */
    saveSidebarState(collapsed) {
        try {
            const key = this.getSidebarStorageKey();
            localStorage.setItem(key, JSON.stringify(collapsed));
        } catch (error) {
            console.warn('Failed to save sidebar state:', error);
        }
    }

    /**
     * Load sidebar state from localStorage
     */
    loadSidebarState() {
        try {
            const key = this.getSidebarStorageKey();
            const saved = localStorage.getItem(key);
            return saved !== null ? JSON.parse(saved) : null;
        } catch (error) {
            console.warn('Failed to load sidebar state:', error);
            return null;
        }
    }

    /**
     * Get storage key for sidebar state (allows multiple apps on same domain)
     */
    getSidebarStorageKey() {
        // Use app title/name to create unique key
        const appKey = this.title ? this.title.replace(/\s+/g, '_').toLowerCase() : 'portal_app';
        return `${appKey}_sidebar_collapsed`;
    }

    /**
     * Apply saved sidebar state to the UI
     */
    applySidebarState(container = null) {
        if (!container) {
            container = document.querySelector('.portal-container');
        }

        if (!container) return;

        if (this.sidebarCollapsed) {
            container.classList.add('collapse-sidebar');
        } else {
            container.classList.remove('collapse-sidebar');
        }
    }

    /**
     * Clear saved sidebar state
     */
    clearSidebarState() {
        try {
            const key = this.getSidebarStorageKey();
            localStorage.removeItem(key);
        } catch (error) {
            console.warn('Failed to clear sidebar state:', error);
        }
    }

    async changePassword() {
        const data = await this.showForm({
            title: "Change Password",
            fields: [
                {
                    name: 'current_password', type: 'password',
                    label: 'Current Password', required: true,
                    showToggle: true,               // default, can omit
                    strengthMeter: true,
                    capsLockWarning: true,

                },
                {
                    name: 'new_password', type: 'password', label: 'New Password', required: true,
                    showToggle: true,
                    passwordUsage: 'new',           // sets autocomplete to 'new-password'

                    strengthMeter: true,
                    capsLockWarning: true,
                    attributes: {
                      // optional, override autocomplete if needed
                      autocomplete: 'new-password'
                    }
                },
                {
                    name: 'confirm_password', type: 'password', label: 'Confirm Password', required: true,
                    showToggle: true,
                    passwordUsage: 'new',           // sets autocomplete to 'new-password'

                    strengthMeter: true,
                    capsLockWarning: true,
                    attributes: {
                      // optional, override autocomplete if needed
                      // autocomplete: 'new-password'
                    }
                }
            ],
            submitLabel: 'Change Password'
        });
        if (data) {
            if (data.new_password === data.confirm_password) {
                // Perform password change logic here
                const resp = await this.activeUser.save(data);
                if (resp.status === 200) {
                    this.toast.success('Password changed successfully');
                } else {
                    this.toast.error('Failed to change password');
                }
            } else {
                this.toast.error('Passwords do not match');
            }
        }
    }

    onPortalAction(action) {
        switch (action.action) {
            case 'logout':
                this.tokenManager.clearTokens();
                this.rest.clearAuth();
                this.setActiveUser(null);
                break;
            case 'profile':
                this.showProfile();
                break;
            case 'change-password':
                this.changePassword();
                break;
            case 'theme-settings':
                this.showThemeSettings();
                break;
            // Legacy direct-set actions — kept as a public API for consumers
            // that wired their own buttons to these (the framework's auto-
            // injected menu now uses `theme-settings` instead).
            case 'theme-light':
                this.setTheme('light');
                this.toast?.success?.('Switched to light theme.');
                break;
            case 'theme-dark':
                this.setTheme('dark');
                this.toast?.success?.('Switched to dark theme.');
                break;
            case 'theme-system':
                this.setTheme('system');
                this.toast?.success?.('Theme follows system preference.');
                break;
            default:
                // App-defined actions (e.g. menu items wired by the consuming
                // app) also travel on `portal:action`; the app handles them in
                // its own listener, so the framework must not warn here.
                break;
        }
    }

    /**
     * Open the framework's theme-settings dialog — Light / Dark / System
     * radios wired to `app.setTheme()`. Wired to the auto-injected
     * `Theme settings` usermenu item, but also callable from any
     * consumer hook (e.g. a sidebar gear icon).
     *
     * Returns the underlying `Modal.dialog` promise so callers can await
     * the close.
     */
    showThemeSettings() {
        const opt = (value, label, icon) => {
            const checked = this.getTheme?.() === value ? 'checked' : '';
            return `
                <label class="d-flex align-items-center gap-2 px-3 py-2 rounded" style="cursor: pointer;"
                       data-theme-row="${value}">
                    <input type="radio" name="mojo-theme" value="${value}" class="form-check-input m-0" ${checked}>
                    <i class="${icon}"></i>
                    <span>${label}</span>
                </label>
            `;
        };

        return Modal.dialog({
            title: 'Theme settings',
            size: 'sm',
            body: `
                <div>
                    <div class="text-uppercase small text-muted mb-2" style="letter-spacing: 0.08em;">Theme</div>
                    <div class="d-flex flex-column gap-1" data-settings-section="theme">
                        ${opt('light',  'Light',  'bi-sun')}
                        ${opt('dark',   'Dark',   'bi-moon-stars')}
                        ${opt('system', 'System', 'bi-circle-half')}
                    </div>
                </div>
            `,
            buttons: [{ text: 'Done', class: 'btn-primary', dismiss: true }],
            onShown: (event) => {
                const root = event?.target;
                if (!root) return;
                const highlight = (value) => {
                    root.querySelectorAll('[data-theme-row]').forEach(row => {
                        row.classList.toggle('bg-body-secondary', row.dataset.themeRow === value);
                    });
                };
                highlight(this.getTheme?.() || 'system');
                root.querySelectorAll('input[name="mojo-theme"]').forEach(el => {
                    el.addEventListener('change', () => {
                        this.setTheme(el.value);
                        highlight(el.value);
                    });
                });
            },
        });
    }

    async showProfile() {
        if (!this.activeUser) {
            this.showError("No user is currently logged in");
            return;
        }

        try {
            const { UserProfileView } = await import('@ext/user-profile/index.js');
            const profileView = new UserProfileView({ model: this.activeUser });

            await Modal.show(profileView, {
                eyeBrow: 'User Profile',
                size: 'lg',
                noBodyPadding: true,
                centered: false
            });
        } catch (error) {
            console.error('Error showing profile:', error);
            this.showError('Failed to load profile');
        }
    }

    async showPasskeySetup() {
        if (localStorage.getItem('passkey_setup_dismissed')) return;

        try {
            const { PasskeySetupView } = await import('@ext/user-profile/index.js');
            const setupView = new PasskeySetupView();

            setupView.on('dismiss', () => {
                // Close the dialog by finding and clicking dismiss
                const dialog = setupView.element?.closest('.modal');
                if (dialog) {
                    const bsModal = bootstrap.Modal.getInstance(dialog);
                    if (bsModal) bsModal.hide();
                }
            });

            await Modal.show(setupView, {
                size: 'sm',
                centered: true,
                buttons: []
            });
        } catch (error) {
            console.error('Error showing passkey setup:', error);
        }
    }

    /**
     * Clean up portal resources
     */
    async destroy() {
        // Clean up event listeners
        // Clear active group
        this.activeGroup = null;

        // Clean up portal resources
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
        }

        // Destroy components using View lifecycle
        if (this.topbar) {
            await this.topbar.destroy();
            this.topbar = null;
            this.topnav = null;
        }

        if (this.sidebar) {
            await this.sidebar.destroy();
            this.sidebar = null;
        }

        // Call parent destroy
        await super.destroy();
    }

    /**
     * Static factory method
     */
    static create(config = {}) {
        return new PortalApp(config);
    }
}
